// API client for D1 database operations
import type { HistoryEntry, Template, AnalyticsEvent } from '../types';

const API_BASE = '/api';

// History operations
export async function addHistoryEntry(entry: HistoryEntry): Promise<void> {
  await fetch(`${API_BASE}/history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: entry.id,
      query: entry.query,
      response: entry.response,
      responseTime: entry.responseTime,
    }),
  });
}

export async function getHistoryEntries(limit = 100): Promise<HistoryEntry[]> {
  const response = await fetch(`${API_BASE}/history?limit=${limit}`);
  const data = await response.json();
  return data.map((entry: HistoryEntry & { timestamp: string }) => ({
    ...entry,
    timestamp: new Date(entry.timestamp),
  }));
}

export async function getHistoryEntry(id: string): Promise<HistoryEntry | undefined> {
  const entries = await getHistoryEntries(1000);
  return entries.find(e => e.id === id);
}

export async function deleteHistoryEntry(id: string): Promise<void> {
  await fetch(`${API_BASE}/history?id=${id}`, { method: 'DELETE' });
}

export async function clearHistory(): Promise<void> {
  const entries = await getHistoryEntries(1000);
  await Promise.all(entries.map(e => deleteHistoryEntry(e.id)));
}

export async function updateHistoryCopied(id: string, platform: string): Promise<void> {
  await fetch(`${API_BASE}/history-copy`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, platform }),
  });
}

// Template operations
export async function addTemplate(template: Template): Promise<void> {
  await fetch(`${API_BASE}/templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: template.id,
      name: template.name,
      category: template.category,
      query: template.query,
    }),
  });
}

export async function getTemplates(): Promise<Template[]> {
  const response = await fetch(`${API_BASE}/templates`);
  const data = await response.json();
  return data.map((template: Template & { createdAt: string; updatedAt: string }) => ({
    ...template,
    createdAt: new Date(template.createdAt),
    updatedAt: new Date(template.updatedAt),
  }));
}

export async function getTemplatesByCategory(category: string): Promise<Template[]> {
  const response = await fetch(`${API_BASE}/templates?category=${encodeURIComponent(category)}`);
  const data = await response.json();
  return data.map((template: Template & { createdAt: string; updatedAt: string }) => ({
    ...template,
    createdAt: new Date(template.createdAt),
    updatedAt: new Date(template.updatedAt),
  }));
}

export async function updateTemplate(id: string, updates: Partial<Template>): Promise<void> {
  await fetch(`${API_BASE}/templates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...updates }),
  });
}

export async function deleteTemplate(id: string): Promise<void> {
  await fetch(`${API_BASE}/templates?id=${id}`, { method: 'DELETE' });
}

export async function incrementTemplateUsage(id: string): Promise<void> {
  await fetch(`${API_BASE}/templates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, incrementUsage: true }),
  });
}

// Analytics operations
export async function addAnalyticsEvent(event: AnalyticsEvent): Promise<void> {
  await fetch(`${API_BASE}/analytics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: event.id,
      type: event.type,
      platform: event.platform,
      confidence: event.confidence,
      responseTime: event.responseTime,
    }),
  });
}

export async function getAnalyticsEvents(days = 30): Promise<AnalyticsEvent[]> {
  const response = await fetch(`${API_BASE}/analytics?days=${days}`);
  const data = await response.json();
  return data.map((event: AnalyticsEvent & { timestamp: string }) => ({
    ...event,
    timestamp: new Date(event.timestamp),
  }));
}

export async function clearAnalytics(): Promise<void> {
  // Not implemented - analytics should persist
}
